import { _getLogger } from '@doxyme/analytics';
import { Specs, type PostMessageContents } from '@doxyme/extensions-common';
import type Instance from '../instance';
import Runtime from './runtime';

const logger = _getLogger('iframeRuntime', {
  component: 'extensions',
  lib: 'bridge',
});

const Actions = Specs.IframedRuntime.Actions;
type Constraints = Specs.Constraints;

type AppletConfig = Specs.Applet.Config;

export function buildIframe(
  runtimeId: string,
  url: string,
  permissions: string[] = [],
): HTMLIFrameElement {
  const frame = document.createElement('iframe');

  frame.id = runtimeId;
  /**
   * Attach querary parameters:
   * @param {appletId} - same as runtimeId for backwards compatibility w/ toolkit
   * @param {bridgeOrigin} - parent window origin to keep messaging between parent and iFrame secure
   */
  frame.src = `${url}?appletId=${runtimeId}&bridgeOrigin=${window.location.origin}`;

  frame.referrerPolicy = 'origin-when-cross-origin';

  frame.allow = permissions.join('; ');

  frame.style.display = 'none';
  frame.style.flex = '1 1 auto';
  frame.style.border = 'transparent'; // 'none';
  frame.style.background = 'transparent';
  frame.style.width = '100%';
  frame.style.height = '100%';

  return frame;
}

export class IframeRuntime extends Runtime {
  readonly frame: HTMLIFrameElement;

  readonly url: URL;

  readonly config: AppletConfig;

  private paused = true;

  private readonly hasDynamicHeight: boolean;

  eventQueue: PostMessageContents[] = [];

  constructor(
    instance: Instance,
    config: AppletConfig,
    hasDynamicHeight = false,
  ) {
    const runtimeId = IframeRuntime.runtimeId(instance, config);

    super(runtimeId, instance);

    this.hasDynamicHeight = hasDynamicHeight;
    this.config = config;
    this.frame = buildIframe(runtimeId, config.url, config.permissions);
    this.url = new URL(this.frame.src);
    this.subscribeFrameMessage();
  }

  override destroy(): void {
    this.unsubscribeFrameMessage();
    this.releaseFocus();
    this.frame.remove();
  }

  /**
   * On mobile device if focus is not released from any form elements the virtual keyboard will
   * remain on screen even after the Iframe has been removed from the dom.
   */
  private releaseFocus(): void {
    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLElement && activeElement.id === this.id) {
      activeElement.blur();
    }
  }

  notify(tuple: Constraints['eventPayloadTuple']): void {
    const [type, payload] = tuple;

    const message: PostMessageContents = {
      appletId: this.id,
      type,
      payload,
    };

    if (this.paused) {
      this.eventQueue.push(message);
      logger.info('Frame is paused - holding events in queue', type);
      return;
    }

    this.frame.contentWindow?.postMessage(message, this.url.origin);
  }

  subscribeFrameMessage(): void {
    // FIXME: remove window handle
    this.controller = new AbortController();
    window.addEventListener('message', this.handleMessage.bind(this), {
      signal: this.controller.signal,
    });
  }

  private handleMessage(event: MessageEvent<PostMessageContents>): void {
    if (event.origin !== this.url.origin || event.data.appletId !== this.id) {
      return;
    }

    switch (event.data.type) {
      case Actions.Ready: {
        this.ready();

        // trigger system event with capabilities online
        this.instance.getBridge().notifyCapabilitiesOnline(this.instance);
        return;
      }
      case Actions.Resume: {
        this.resume();
        return;
      }
      case Actions.Pause: {
        this.pause();
        return;
      }
      case Actions.SetHeight: {
        this.setHeight(event.data.payload);
        return;
      }
      default: // NOOP
    }

    const spec = Specs.byActionType(event.data.type);
    if (!spec) {
      logger.warn(`Ignoring invalid capability action: ${event.data.type}`);
      return;
    }

    if (!this.instance.usesCapability(spec.id)) {
      logger.warn(`Rejecting action for unregistered capability: ${spec.id}`);
      return;
    }

    this.instance.receive([
      event.data.type,
      event.data.payload,
    ] as Constraints['actionPayloadTuple']);
  }

  unsubscribeFrameMessage(): void {
    // Remove window's event listener
    if (this.controller) {
      this.controller.abort();
      this.controller = undefined;
    }
  }

  emptyQueue(): void {
    for (const message of this.eventQueue) {
      this.frame.contentWindow?.postMessage(message, this.url.origin);
    }
    this.eventQueue = [];
  }

  isShown(): boolean {
    return this.frame.style.display !== 'none';
  }

  private ready(): void {
    this.resume();
    this.show();
  }

  private pause(): void {
    this.paused = true;
  }

  private resume(): void {
    this.paused = false;
    this.emptyQueue();
  }

  private setHeight(height: unknown): void {
    if (!this.hasDynamicHeight) {
      return;
    }

    if (typeof height !== 'string') {
      logger.warn(
        '[setHeight] height will not be set as payload is not a string',
        { height },
      );
      return;
    }

    this.frame.style.height = height;
  }

  show(): void {
    this.frame.style.display = 'flex';
  }

  hide(): void {
    this.frame.style.display = 'none';
  }
}
